import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  AppWindow,
  Laptop,
  Wallet,
  ArrowRight,
  Shield,
  Key,
  LogIn,
  CheckCircle,
  AlertCircle,
  RefreshCw,
} from 'lucide-react'
import { PageHeader } from '@/components/layout'
import { Card, CardHeader, CardTitle, CardContent, Button, Skeleton } from '@/components/ui'
import { useApiClient, api, type AccountSummary, type ActivityItem } from '@/api'

export function DashboardPage() {
  const client = useApiClient()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [summary, setSummary] = useState<AccountSummary | null>(null)
  const [activity, setActivity] = useState<ActivityItem[]>([])

  const loadData = async () => {
    setLoading(true)
    setError(null)
    try {
      const [summaryRes, activityRes] = await Promise.all([
        api.getSummary(client),
        api.getActivity(client, 5),
      ])
      setSummary(summaryRes)
      setActivity(activityRes.items)
    } catch (err) {
      console.error('Failed to load dashboard data:', err)
      setError('Failed to load dashboard data')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadData()
  }, [])

  if (loading) {
    return (
      <div>
        <PageHeader title="Dashboard" description="Your account at a glance" />
        <div className="grid gap-4 md:grid-cols-3 mb-8">
          {[1, 2, 3].map((i) => (
            <Card key={i}>
              <CardContent className="pt-6">
                <Skeleton className="h-4 w-24 mb-2" />
                <Skeleton className="h-8 w-16" />
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div>
        <PageHeader title="Dashboard" description="Your account at a glance" />
        <Card>
          <CardContent className="py-8 text-center">
            <p className="text-[var(--text-secondary)] mb-4">{error}</p>
            <Button onClick={loadData}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Retry
            </Button>
          </CardContent>
        </Card>
      </div>
    )
  }

  const securityStatus = [
    { label: 'Email verified', completed: summary?.email_verified ?? false },
    { label: `MFA enabled${summary?.mfa_method ? ` (${summary.mfa_method.toUpperCase()})` : ''}`, completed: summary?.mfa_enabled ?? false },
    { label: 'Passkey configured', completed: summary?.mfa_method === 'webauthn' },
  ]

  return (
    <div>
      <PageHeader
        title="Dashboard"
        description="Your account at a glance"
      />

      {/* Stats cards */}
      <div className="grid gap-4 md:grid-cols-3 mb-8">
        <StatCard
          title="Connected Apps"
          value={summary?.authorized_apps ?? 0}
          icon={AppWindow}
          href="/apps"
          linkText="View all"
        />
        <StatCard
          title="Active Sessions"
          value={summary?.active_sessions ?? 0}
          icon={Laptop}
          href="/account/security"
          linkText="Manage"
        />
        <StatCard
          title="Linked Accounts"
          value={summary?.linked_accounts ?? 0}
          icon={Shield}
          href="/account/security"
          linkText="Manage"
        />
      </div>

      {/* Main content grid */}
      <div className="grid gap-6 lg:grid-cols-2">
        {/* Recent Activity */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>Recent Activity</CardTitle>
              <Button variant="ghost" size="sm" asChild>
                <Link to="/account/activity" className="flex items-center gap-1">
                  View all <ArrowRight className="h-3 w-3" />
                </Link>
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {activity.length > 0 ? (
              <div className="space-y-4">
                {activity.map((item) => (
                  <div key={item.id} className="flex items-start gap-3">
                    <div className="rounded-full bg-[var(--bg-elevated-2)] p-2">
                      <ActivityIcon type={item.action} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-[var(--text-primary)]">{item.description}</p>
                      <p className="text-xs text-[var(--text-muted)]">
                        {formatTimeAgo(item.created_at)}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-[var(--text-secondary)]">No recent activity</p>
            )}
          </CardContent>
        </Card>

        {/* Security & Quick Actions */}
        <div className="space-y-6">
          {/* Security Status */}
          <Card>
            <CardHeader>
              <CardTitle>Security Status</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {securityStatus.map((item, index) => (
                  <div key={index} className="flex items-center gap-2">
                    {item.completed ? (
                      <CheckCircle className="h-4 w-4 text-[var(--success)]" />
                    ) : (
                      <AlertCircle className="h-4 w-4 text-[var(--text-muted)]" />
                    )}
                    <span className={item.completed ? 'text-[var(--text-primary)]' : 'text-[var(--text-secondary)]'}>
                      {item.label}
                    </span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Quick Actions */}
          <Card>
            <CardHeader>
              <CardTitle>Quick Actions</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <Button variant="secondary" className="w-full justify-start" asChild>
                  <Link to="/account/security">
                    <Key className="mr-2 h-4 w-4" />
                    {summary?.mfa_enabled ? 'Manage MFA' : 'Enable MFA'}
                  </Link>
                </Button>
                <Button variant="secondary" className="w-full justify-start" asChild>
                  <Link to="/api-keys">
                    <Key className="mr-2 h-4 w-4" />
                    Create API key
                  </Link>
                </Button>
                <Button variant="secondary" className="w-full justify-start" asChild>
                  <Link to="/account/security">
                    <Laptop className="mr-2 h-4 w-4" />
                    Manage sessions
                  </Link>
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  )
}

interface StatCardProps {
  title: string
  value: string | number
  icon: React.ElementType
  href: string
  linkText: string
}

function StatCard({ title, value, icon: Icon, href, linkText }: StatCardProps) {
  return (
    <Card>
      <CardContent className="pt-6">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-sm text-[var(--text-secondary)]">{title}</p>
            <p className="text-3xl font-semibold text-[var(--text-primary)] mt-1">{value}</p>
          </div>
          <div className="rounded-full bg-[var(--bg-elevated-2)] p-3">
            <Icon className="h-5 w-5 text-[var(--text-secondary)]" />
          </div>
        </div>
        <Link
          to={href}
          className="inline-flex items-center gap-1 mt-4 text-sm text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-colors"
        >
          {linkText} <ArrowRight className="h-3 w-3" />
        </Link>
      </CardContent>
    </Card>
  )
}

function ActivityIcon({ type }: { type: string }) {
  const iconClass = "h-4 w-4 text-[var(--text-secondary)]"

  if (type.includes('login') || type.includes('session')) {
    return <LogIn className={iconClass} />
  }
  if (type.includes('app') || type.includes('authorize')) {
    return <AppWindow className={iconClass} />
  }
  if (type.includes('mfa') || type.includes('security') || type.includes('password')) {
    return <Shield className={iconClass} />
  }
  if (type.includes('key')) {
    return <Key className={iconClass} />
  }
  return <Shield className={iconClass} />
}

function formatTimeAgo(dateString: string): string {
  const date = new Date(dateString)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffMins = Math.floor(diffMs / 60000)
  const diffHours = Math.floor(diffMs / 3600000)
  const diffDays = Math.floor(diffMs / 86400000)

  if (diffMins < 1) return 'Just now'
  if (diffMins < 60) return `${diffMins} min ago`
  if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`
  if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`
  return date.toLocaleDateString()
}
